import React, { useState, useEffect, useCallback } from 'react';
import { generateClient } from 'aws-amplify/data';
import { parseLambdaResponse } from '../../utils/parseLambdaResponse';
import { isPublished as isEventPublished } from '@shared/competitionRules';
import PublicHeader from '../PublicHeader';
import { formatClubDate } from '../../utils/clubTime';

const client = generateClient({ authMode: 'apiKey' });

const CATEGORY_ORDER = ['FLATHEAD', 'BREAM', 'JEWFISH', 'MANGROVE_JACK', 'WHITING', 'TAILOR', 'TREVALLY', 'MYSTERY_FISH'];
const CATEGORY_LABELS: Record<string, string> = {
  FLATHEAD: 'Flathead',
  BREAM: 'Bream',
  JEWFISH: 'Jewfish',
  MANGROVE_JACK: 'Mangrove Jack',
  WHITING: 'Whiting',
  TAILOR: 'Tailor',
  TREVALLY: 'Trevally',
  MYSTERY_FISH: 'Mystery Fish 🎣',
};

interface PublicLeaderboardProps {
  eventId: string;
}

const PublicLeaderboard: React.FC<PublicLeaderboardProps> = ({ eventId }) => {
  const [event, setEvent] = useState<any>(null);
  const [leaderboard, setLeaderboard] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const eventResult = await client.models.Event.get({ id: eventId });
      setEvent(eventResult.data);

      const result = await (client.queries as any).calculateLeaderboard({ eventId });
      const data = parseLambdaResponse(result?.data);
      setLeaderboard(data);
    } catch (err: any) {
      console.error('Error loading results:', err);
      setError('Failed to load results');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-blue-100 flex items-center justify-center">
        <div className="text-gray-600">Loading results...</div>
      </div>
    );
  }

  const isPublished = isEventPublished(event);

  if (!isPublished) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-blue-100 py-12 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="text-6xl mb-4">🎣</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Results Not Yet Published</h1>
            <p className="text-gray-600">
              The results for this competition haven't been published yet. Check back later!
            </p>
            <button
              onClick={() => { window.history.pushState({}, '', '/'); window.dispatchEvent(new PopStateEvent('popstate')); }}
              className="mt-6 px-6 py-3 bg-fishing-blue text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Key names must match calculate-leaderboard/handler.ts's response exactly.
  // These previously read `categories`/`overallFishCount`, which the Lambda has
  // never returned — so the public results page rendered no categories at all
  // and always showed 0 total fish.
  const categoryRankings = leaderboard?.categoryRankings || {};
  const overallFishCount = leaderboard?.totalFishCount || 0;

  // Mystery fish is a separate top-level array; merge it into the
  // MYSTERY_FISH slot CATEGORY_ORDER already reserves. The Lambda only
  // populates it once results are published (or for admins).
  const mysteryFishRankings = leaderboard?.mysteryFishRankings || [];
  const categories: Record<string, any[]> = {
    ...categoryRankings,
    ...(mysteryFishRankings.length > 0 ? { MYSTERY_FISH: mysteryFishRankings } : {}),
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-blue-100">
      <PublicHeader title="Competition Results" />
      <div className="py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6 text-center">
          <img src="/tumbulgum-logo.png" alt="Tumbulgum Fishing Club" className="h-12 mx-auto mb-3" />
          <h1 className="text-2xl font-bold text-gray-900">{event?.title || 'Competition Results'}</h1>
          <p className="text-gray-600 text-sm mt-1">
            {event?.eventDate ? formatClubDate(event.eventDate, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : ''}
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-red-800">{error}</div>
        )}

        {/* Per-category rankings */}
        {CATEGORY_ORDER.map((catKey) => {
          const entries = categories[catKey];
          if (!entries || entries.length === 0) return null;
          return (
            <div key={catKey} className="bg-white rounded-lg shadow-lg mb-4 overflow-hidden">
              <div className="bg-fishing-blue px-4 py-3">
                <h2 className="text-white font-semibold">{CATEGORY_LABELS[catKey] || catKey}</h2>
              </div>
              {entries.map((entry: any, idx: number) => {
                const position = idx + 1;
                const isTop3 = position <= 3;
                return (
                  <div
                    key={entry.competitorName + idx}
                    className={`flex items-center justify-between px-4 py-3 border-b border-gray-100 last:border-b-0 ${
                      position === 1 ? 'bg-yellow-50' : position === 2 ? 'bg-gray-50' : position === 3 ? 'bg-amber-50' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`w-8 h-8 flex items-center justify-center rounded-full text-sm font-bold ${
                        position === 1 ? 'bg-yellow-400 text-white' :
                        position === 2 ? 'bg-gray-300 text-white' :
                        position === 3 ? 'bg-amber-500 text-white' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {position}
                      </span>
                      <span className={`font-medium ${isTop3 ? 'text-gray-900' : 'text-gray-700'}`}>
                        {entry.competitorName}
                      </span>
                    </div>
                    <span className={`text-lg font-bold ${isTop3 ? 'text-fishing-blue' : 'text-gray-600'}`}>
                      {entry.lengthCm}cm
                    </span>
                  </div>
                );
              })}
            </div>
          );
        })}

        {/* Overall fish count */}
        <div className="bg-white rounded-lg shadow-lg p-6 text-center">
          <div className="text-3xl font-bold text-fishing-blue">{overallFishCount}</div>
          <div className="text-gray-600 text-sm">Total Fish Caught</div>
        </div>

        {/* Champion Points */}
        {isPublished && leaderboard?.championPoints && (
          <div className="bg-white rounded-lg shadow-lg mt-4 overflow-hidden">
            <div className="bg-yellow-500 px-4 py-3">
              <h2 className="text-white font-semibold">🏆 Champion Points</h2>
            </div>
            <div className="p-4">
              {/* Confirmed champions */}
              {(event?.seniorChampionName || event?.juniorChampionName) && (
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-center">
                  {event?.seniorChampionName && (
                    <div className="text-sm font-medium text-yellow-800">
                      🏆 Senior Champion: <span className="font-bold">{event.seniorChampionName}</span> ({event.seniorChampionPoints || 0} pts)
                    </div>
                  )}
                  {event?.juniorChampionName && (
                    <div className="text-sm font-medium text-yellow-800 mt-1">
                      🏆 Junior Champion: <span className="font-bold">{event.juniorChampionName}</span> ({event.juniorChampionPoints || 0} pts)
                    </div>
                  )}
                </div>
              )}
              {/* Senior standings */}
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Senior Division</h3>
              {leaderboard.championPoints.seniorStandings?.length > 0 ? (
                <table className="w-full text-sm mb-4">
                  <thead><tr className="border-b text-left text-gray-500"><th className="py-1">Name</th><th className="py-1 text-right">Points</th></tr></thead>
                  <tbody>
                    {leaderboard.championPoints.seniorStandings.map((s: any, i: number) => (
                      <tr key={i} className="border-b last:border-b-0"><td className="py-1">{s.competitorName}</td><td className="py-1 text-right font-medium">{s.totalPoints}</td></tr>
                    ))}
                  </tbody>
                </table>
              ) : <p className="text-sm text-gray-500 italic mb-4">No points scored</p>}
              {/* Junior standings */}
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Junior Division</h3>
              {leaderboard.championPoints.juniorStandings?.length > 0 ? (
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left text-gray-500"><th className="py-1">Name</th><th className="py-1 text-right">Points</th></tr></thead>
                  <tbody>
                    {leaderboard.championPoints.juniorStandings.map((s: any, i: number) => (
                      <tr key={i} className="border-b last:border-b-0"><td className="py-1">{s.competitorName}</td><td className="py-1 text-right font-medium">{s.totalPoints}</td></tr>
                    ))}
                  </tbody>
                </table>
              ) : <p className="text-sm text-gray-500 italic">No points scored</p>}
            </div>
          </div>
        )}

        {/* Back link */}
        <div className="text-center mt-6">
          <button
            onClick={() => { window.history.pushState({}, '', '/'); window.dispatchEvent(new PopStateEvent('popstate')); }}
            className="text-fishing-blue hover:underline text-sm"
          >
            ← Back to Home
          </button>
        </div>
      </div>
      </div>
    </div>
  );
};

export default PublicLeaderboard;
